import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import _ from 'lodash'
import { format, compareDesc } from 'date-fns'

import { ReactComponent as GlassesSVG } from 'assets/svg/glasses.svg'
import { ReactComponent as LensSVG } from 'assets/svg/lens.svg'

import { Color, Gap, NINAL_SHORTER_NAME, Responsive, Size } from 'const'
import { Banner, Button, ChevronIcon, Logout, OldProduct } from 'components/shard'
import { Modal } from 'components/mobile/common/Modal'
import Alert from 'components/Alert'
import { Link } from 'react-router-dom'

import { useServiceHook } from 'hooks'
import { formatTZ } from 'utils/datetime'
import WrapperLink from 'components/shard/WrapperLink'

function ServiceDetail() {
  const {
    // service関連
    service,
    serviceErrors,
    getService,
    toTanakaSpecialURL,
    isServiceDetailLoading,
    resetErrors,

    // creditCard関連
    creditCard,
    creditCardErrors,
    getCreditCard,

    // モーダル関連
    modalDisplay,
    setModalDisplay,
    setContractId,
    close,
    link,

    // product
    goToProductInfo,
  } = useServiceHook()

  useEffect(() => {
    getService()
    getCreditCard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const headingElement = (
    <Heading>
      サービス内容やよくある質問は
      <HeadingLink as="div" role="button" tabIndex={0} onClick={toTanakaSpecialURL}>
        コチラ
      </HeadingLink>
    </Heading>
  )

  return (
    <React.Fragment>
      {headingElement}
      {isServiceDetailLoading && <ServiceDetailIsRequesting />}
      {(serviceErrors.length > 0 || creditCardErrors.length > 0) && !isServiceDetailLoading && (
        <Alert
          type="error"
          message="エラー"
          description={serviceErrors.concat(creditCardErrors).join('\n')}
          onClose={resetErrors}
        />
      )}
      {!_.isEmpty(service) && !isServiceDetailLoading && (
        <ServiceDetailDisplay
          service={service}
          creditCard={creditCard}
          modalDisplay={modalDisplay}
          setModalDisplay={setModalDisplay}
          link={link}
          close={close}
          setContractId={setContractId}
          goToProductInfo={goToProductInfo}
        />
      )}
      <Container>
        <Banner />
        <Logout />
      </Container>
    </React.Fragment>
  )
}

function ServiceDetailIsRequesting() {
  return (
    <IsRequestingWrapper>
      <IsRequesting>ご利用状況、およびお支払い状況を取得中</IsRequesting>
    </IsRequestingWrapper>
  )
}

function ServiceDetailDisplay({
  service,
  creditCard,
  modalDisplay,
  setModalDisplay,
  link,
  close,
  setContractId,
  goToProductInfo,
}) {
  useEffect(() => {
    if (!_.isEmpty(service)) {
      // モーダルのリンク先セット
      setContractId(service.dev_contractid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service])

  // 概要
  const info = service.ninal_subscription_application
  const serviceName = NINAL_SHORTER_NAME[service.dev_service_name]
  const stateNo = service.dev_state_no

  // 利用者契約者
  const contractor = info.contractor
  const user = info.user

  // 購入明細
  const newItem = info.new_item
  const oldItems = info.old_items
  const willReturnItem = info.will_return_item

  // 判定式
  const isNewFrameExist = newItem.frames.length > 0
  const isNewLensExist = newItem.lenses.length > 0
  const isOldItemsExist = oldItems.length > 0
  const newItemArray = _.flatMap(_.values(newItem))
  const willReturnItemArray = _.flatMap(_.values(willReturnItem))
  const isItemsExist = newItemArray.length > 0 || willReturnItemArray.length > 0

  // credit card
  const isCreditCartExist = !_.isEmpty(creditCard)

  const orders = {
    右: 2,
    左: 3,
  }

  const groupOldItems = isOldItemsExist
    ? _.chain(
        oldItems
          .map((it) => ({ ...it, dev_return_date_fm: formatTZ(it.dev_return_date) }))
          .sort((a, b) => compareDesc(new Date(a.dev_return_date), new Date(b.dev_return_date)))
      )
        .groupBy(
          (item) =>
            `${item.dev_return_date_fm}_${item.dev_return_code || item.dev_return_reason.replace('NINAL', '')}_${
              item.dev_box_number
            }`
        )
        .map((items, key) => {
          const keys = key.split('_')

          return {
            dev_return_date: keys[0],
            dev_return_code: keys[1],
            dev_box_number: keys[2],
            products: items
              .map((item) => ({ ...item, pos: item.dev_rl ? orders[item.dev_rl] : 1 }))
              .sort((a, b) => a.pos - b.pos),
          }
        })
        .value()
    : []

  // ご利用中の貸出日算出(現在ご利用中の商品の中で一番最新の貸出日を取得する)
  const salesDate = (() => {
    const concatenatedItems = newItem.frames.concat(newItem.lenses)

    if (concatenatedItems.length > 0) {
      // 全部nullなら空文字にする
      if (concatenatedItems.every((item) => item.dev_delivery_date === null)) {
        return
      }

      const dateArray = concatenatedItems.map((item) => new Date(item.dev_sales_date))
      dateArray.sort(compareDesc)

      return format(dateArray[0], 'yyyy年M月d日貸出')
    }

    return ''
  })()

  const newItemsPurchaseDetailIds = _.flatMap(_.values(newItem)).map((item) => item.dev_purchase_detailid)

  const getOldItemsPurchaseDetailsId = (items) => {
    return items.map((item) => item.dev_purchase_detailid)
  }

  const canSubscriptionAvailableShow =
    serviceName !== 'NINAL step' && (stateNo === 277670000 || (stateNo === 277670003 && isItemsExist))

  const subscriptionAvailableText =
    (isNewFrameExist || isNewLensExist) && isCreditCartExist ? `${info.available_num}回` : '-'

  return (
    <React.Fragment>
      <Modal
        title="クレジットカードの登録"
        text="クレジットカードを登録ください。"
        caution="プリペイドやデビットの場合、申込直後に請求される場合があります。"
        display={modalDisplay}
        overlayOnClick={close}
        buttonOnClick={link}
      />
      <ServiceBlock>
        <Title>
          <GlassesSVG />
          <ServiceName>{serviceName}</ServiceName>
        </Title>
        <ContractNum>契約番号: {info.dev_contract_no ? info.dev_contract_no : '-'}</ContractNum>
        <ListLabelArea>
          <ListLabel>お客様情報</ListLabel>
        </ListLabelArea>
        <List>
          <NameBox label="ご利用者氏名" name={user.lastname + ` ` + user.firstname} />
          <NameBox label="ご契約者氏名" name={contractor.lastname + ` ` + contractor.firstname} />
        </List>
        {info.is_contractor && (
          <React.Fragment>
            <ListLabelAreaFlex>
              <ListLabel>お支払い方法</ListLabel>
            </ListLabelAreaFlex>
            <PayArea>
              <PayLabel>クレジットカード</PayLabel>
              {!isCreditCartExist ? (
                <PayCaution>登録されていません</PayCaution>
              ) : (
                <PayNumber>
                  <span className="cardno">{creditCard.card_no}</span>
                  <span className="expires">
                    （ 有効期限：{creditCard.month}/{creditCard.year} ）
                  </span>
                </PayNumber>
              )}
            </PayArea>
            <PayButtonWrapper>
              <Link to={`/pay/${service.dev_contractid}`}>
                <Button fontSize="18px" type="button" primary>
                  お支払い状況を確認する
                </Button>
              </Link>
            </PayButtonWrapper>
            <PayButtonWrapper>
              <Button
                fontSize="18px"
                type="button"
                primary
                onClick={_.isEmpty(creditCard) ? () => setModalDisplay(true) : link}
              >
                クレジットカードを{_.isEmpty(creditCard) ? '登録' : '変更'}する
              </Button>
            </PayButtonWrapper>
          </React.Fragment>
        )}
        <ListLabelArea>
          <ListLabel>契約情報</ListLabel>
        </ListLabelArea>
        <ContractList>
          <ContractBox
            label="契約開始日"
            date={info.dev_open_date ? format(new Date(info.dev_open_date), 'yyyy年M月d日') : '-'}
          />
          <ContractBox
            label="契約終了日"
            date={
              info.dev_close_date
                ? format(new Date(info.dev_close_date), 'yyyy年M月d日')
                : info.dev_will_close_date
                ? format(new Date(info.dev_will_close_date), 'yyyy年M月d日')
                : '-'
            }
          />
          {canSubscriptionAvailableShow && (
            <ContractBox
              label="残りの交換回数"
              date={subscriptionAvailableText}
              templateCols="113px 1fr"
              templateRows="26px"
              gridCol={2}
              gridRow={1}
              labelFw={500}
              dateFs={Size.FONT.BIG}
            />
          )}
        </ContractList>
        <ListLabelArea>
          <ListLabel>現在ご利用中のフレーム/レンズ</ListLabel>
        </ListLabelArea>
        {isNewFrameExist || isNewLensExist ? (
          <NewProductBox onClick={() => goToProductInfo(newItemsPurchaseDetailIds)}>
            <ProductList>
              {isNewFrameExist &&
                _.map(newItem.frames, (frame) => {
                  return (
                    <ProductBoxNew
                      key={frame.dev_purchase_detailid}
                      isLens={false}
                      text1={'フレーム'}
                      text2={`${frame.dev_brand} ${frame.abbreviation || ''}`}
                    />
                  )
                })}
              {isNewLensExist &&
                _.map(newItem.lenses, (lens) => {
                  return (
                    <ProductBoxNew
                      key={lens.dev_purchase_detailid}
                      isLens={true}
                      text1={lens.dev_rl === '左' ? 'レンズL' : 'レンズR'}
                      // text2={`${frame.dev_brand} ${frame.abbreviation} ${lens.dev_sph`}
                      text2={`${lens.dev_brand} ${lens.abbreviation || ''}`}
                    />
                  )
                })}
            </ProductList>
            {salesDate && <ProductDate>貸出日: {salesDate}</ProductDate>}
            <Separator />
            <div className="navigation">
              <ChevronIcon />
            </div>
          </NewProductBox>
        ) : (
          <ListNothing paddingVertical>現在ご利用中のフレーム/レンズはありません。</ListNothing>
        )}
        <ListLabelArea>
          <ListLabel>過去ご利用済みのフレーム/レンズ</ListLabel>
        </ListLabelArea>
        <GridBlock two={isOldItemsExist}>
          {isOldItemsExist ? (
            _.map(groupOldItems, (item, index) => {
              return (
                <OldProduct
                  key={index}
                  {...item}
                  onClick={() => {
                    const purchaseDetailIds = getOldItemsPurchaseDetailsId(item.products)
                    goToProductInfo(purchaseDetailIds)
                  }}
                />
              )
            })
          ) : (
            <ListNothing>過去ご利用済みのフレーム/レンズはありません。</ListNothing>
          )}
        </GridBlock>
      </ServiceBlock>
    </React.Fragment>
  )
}

function NameBox({ label, name }) {
  return (
    <NameArea>
      <NameLabel>{label}</NameLabel>
      <Name>{name}</Name>
      <NameLinkArea />
    </NameArea>
  )
}

function ContractBox({ label, date, templateCols, templateRows, gridCol, gridRow, labelFw, dateFs }) {
  return (
    <ContractArea templateCols={templateCols} templateRows={templateRows}>
      <ContractLabel fw={labelFw}>{label}</ContractLabel>
      <ContractDate gridCol={gridCol} gridRow={gridRow} fs={dateFs}>
        {date}
      </ContractDate>
    </ContractArea>
  )
}

function ProductBoxNew({ isLens, text1, text2 }) {
  return (
    <ProductAreaNew>
      <SVGNew>{isLens ? <LensSVG /> : <GlassesSVG />}</SVGNew>
      <ProductText1New>{text1}</ProductText1New>
      {!_.isEmpty(text2) && <ProductText2New>{text2}</ProductText2New>}
    </ProductAreaNew>
  )
}

function ProductBox({ isLens, text1, text2, info1, info2 }) {
  return (
    <ProductArea>
      <SVG>{isLens ? <LensSVG /> : <GlassesSVG />}</SVG>
      <SVGLabel>{text1}</SVGLabel>
      <ProductText1>{text2}</ProductText1>
      <ProductInfo1>{info1}</ProductInfo1>
      <ProductInfo2>{info2}</ProductInfo2>
    </ProductArea>
  )
}

const Separator = styled.hr`
  border-top: 1px solid ${Color.LINE.SHADOW};
  border-bottom: 0;
  margin: 0 24px;
`

const Container = styled.div`
  margin-top: 60px;
`

const Heading = styled.div`
  margin: 0 0 10px;
  font-weight: 500;
  padding: 0 20px;
  font-size: ${Size.FONT.BIG};
`

const HeadingLink = styled(WrapperLink)`
  cursor: pointer;
  display: inline-block;
  text-decoration: underline;
`

const NewProductBox = styled.div`
  border-radius: 10px;
  border: 1px solid ${Color.LINE.NORMAL};
  margin: 16px 24px;
  cursor: pointer;
  position: relative;
  .navigation {
    position: absolute;
    right: 12px;
    top: 40%;
  }
`

const ServiceBlock = styled.div``

const IsRequestingWrapper = styled.div`
  padding: 15px 20px;
  border-radius: 10px;
  border: 1px solid ${Color.LINE.NORMAL};
  margin: 17px 20px 0;
`
const IsRequesting = styled.div`
  font-size: ${Size.FONT.REGULAR};
`
const Title = styled.div`
  display: flex;
  justify-content: center;
  padding: 17px 0 10px;
`
const ServiceName = styled.div`
  font-size: ${Size.FONT.HEADLINE.XL};
  text-align: center;
  margin-left: 10px;
  height: 40px;
  line-height: 40px;
  font-weight: 700;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  display: -webkit-box;
  overflow: hidden;
`
const ContractNum = styled.div`
  font-size: ${Size.FONT.BIG};
  padding-bottom: 34px;
  text-align: center;
  font-weight: 500;
`
// const CautionBox = styled.div`
//   margin-bottom: 25px;
// `
// const Caution = styled.span`
//   font-size: ${Size.FONT.REGULAR};
//   padding: 1px 24px;
// `
const ListLabelArea = styled.div`
  background-color: ${Color.GREY};
  padding: 5px 20px;
  font-weight: 700;
`
const ListLabelAreaFlex = styled.div`
  background-color: ${Color.GREY};
  padding: 5px 20px;
  display: flex;
  justify-content: space-between;
  font-weight: 700;
`
const ListLabel = styled.div`
  grid-column: 1;
  font-size: ${Size.FONT.BIGGER};
  font-weight: 700;
`
const List = styled.div`
  padding: 16px 20px 41px;
`

const GridBlock = styled.div`
  display: grid;
  gap: ${Gap.LG};
  padding: 0 24px;
  margin-top: 24px;
  grid-template-columns: ${(props) => (props.two ? 'repeat(2, 1fr)' : '1fr')};
  @media screen and (max-width: ${Responsive.SM.DOWN}px) {
    grid-template-columns: 1fr;
  }
`
const NameArea = styled.div`
  display: grid;
  grid-template-columns: auto 9px 1fr;
  grid-template-rows: 19px 7px 26px;
  margin-bottom: 24px;

  &:last-of-type {
    margin-bottom: 0;
  }
`
const NameLabel = styled.div`
  font-size: ${Size.FONT.BIG};
  grid-column: 1 / 3;
  grid-row: 1;
  display: grid;
  align-items: center;
  font-weight: 700;
`
const Name = styled.div`
  font-size: ${Size.FONT.BIGGER};
  grid-column: 1;
  grid-row: 3;
  display: grid;
  align-items: center;
  font-weight: 500;
`
const NameLinkArea = styled.div`
  grid-column: 3;
  grid-row: 3;
  display: grid;
  align-items: center;
`
const ContractList = styled.div`
  padding: 20px;
`
const ContractArea = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.templateCols || '1fr'};
  grid-template-rows: ${(props) => props.templateRows || '19px 7px 26px'};
  margin-bottom: 24px;
  gap: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`
const ContractLabel = styled.div`
  grid-column: 1;
  grid-row: 1;
  display: grid;
  align-items: center;
  font-weight: ${(props) => props.fw || 700};
  font-size: ${Size.FONT.BIG};
`
const ContractDate = styled.div`
  grid-column: ${(props) => props.gridCol || 1};
  grid-row: ${(props) => props.gridRow || 3};
  display: grid;
  align-items: center;
  font-size: ${(props) => props.fs || Size.FONT.BIGGER};
  font-weight: 500;
`
const ProductList = styled.div`
  margin: 16px 20px;
`
const ProductArea = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 116px;
  grid-template-rows: 40px 30px 8px 19px;
  font-size: ${Size.FONT.REGULAR};
`
const ProductAreaNew = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr;
  grid-template-rows: 30px 30px;
  margin-bottom: 12px;

  &:last-of-type {
    margin: none;
  }
`
const SVG = styled.div`
  grid-column: 1;
  grid-row: 1 / 2;
  display: grid;
  align-items: center;
`
const SVGLabel = styled.div`
  grid-column: 2 / 4;
  grid-row: 1 / 2;
  display: grid;
  align-self: center;
  color: ${Color.FONT.ANNOTATION};
  font-size: ${Size.FONT.REGULAR};
`
const SVGNew = styled.div`
  grid-column: 1 / 2;
  grid-row: 1 / 3;
  display: grid;
  text-align: left;
`
const ProductText1 = styled.div`
  font-size: ${Size.FONT.NORMAL};
  grid-column: 1 / 4;
  grid-row: 2;
  display: grid;
  align-items: center;
`
const ProductText1New = styled.div`
  font-size: ${Size.FONT.REGULAR};
  grid-column: 2 / 3;
  grid-row: 1;
  display: grid;
  align-items: center;
  font-weight: 500;
`
const ProductText2New = styled.div`
  font-size: ${Size.FONT.BIG};
  grid-column: 2 / 3;
  grid-row: 2;
  display: grid;
  align-items: center;
  font-weight: 700;
`
const ProductInfo1 = styled.div`
  font-size: ${Size.FONT.NORMAL};
  grid-column: 1 / 3;
  grid-row: 4;
  display: grid;
  align-items: center;
  text-align: left;
`
const ProductInfo2 = styled.div`
  font-size: ${Size.FONT.NORMAL};
  text-align: right;
  grid-column: 3 / 4;
  grid-row: 4;
  display: grid;
  align-items: center;
  text-align: right;
`
const ProductDate = styled.div`
  font-size: ${Size.FONT.BIG};
  text-align: left;
  padding-left: 24px;
  margin-bottom: 25px;
  font-weight: 500;
`
const ListNothing = styled.div`
  font-size: ${Size.FONT.NORMAL};
  display: flex;
  padding: 10px 0;
  justify-content: center;
  ${(props) => props.paddingVertical && 'padding: 10px 24px'};
  font-weight: 500;
`
const PayArea = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 19px 7px 26px;
  padding: 13px 20px 26px;
  font-size: ${Size.FONT.NORMAL};
`
const PayLabel = styled.div`
  grid-column: 1;
  grid-row: 1;
  display: grid;
  align-items: center;
  font-weight: 700;
`
const PayCaution = styled.div`
  font-size: ${Size.FONT.NORMAL};
  grid-column: 2;
  display: grid;
  align-items: center;
`
const PayNumber = styled.div`
  font-size: ${Size.FONT.BIGGER};
  grid-column: 1;
  grid-row: 3;
  font-weight: 500;

  .expires {
    font-size: ${Size.FONT.BIG};
    display: inline-block;
  }

  .cardno {
    display: inline-block;
  }
`
const PayButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 15px;
`

ServiceDetail.propTypes = {
  id: PropTypes.number,
}
ServiceDetailDisplay.propTypes = {
  service: PropTypes.object,
  creditCard: PropTypes.object,
  modalDispla: PropTypes.bool,
  setModalDisplay: PropTypes.func,
  link: PropTypes.func,
  close: PropTypes.func,
  setContractId: PropTypes.func,
}
NameBox.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
}
ContractBox.propTypes = {
  label: PropTypes.string,
  date: PropTypes.string,
  templateCols: PropTypes.string,
  templateRows: PropTypes.string,
  gridCol: PropTypes.number,
  gridRow: PropTypes.number,
  labelFw: PropTypes.number,
  dateFs: PropTypes.string,
}
ProductBox.propTypes = {
  img: PropTypes.string,
  text1: PropTypes.string,
  date1: PropTypes.string,
  date2: PropTypes.string,
}

export default ServiceDetail
